<template>
    <div class="pa-4">
        <!-- Speed Config -->
        <template v-if="type.toLowerCase() === 'speed'">
            <div class="text-center mb-4">
                <v-icon size="36" color="orange" class="mb-2">mdi-speedometer</v-icon>
                <h3 class="text-subtitle-1 font-weight-bold">Speed Alert Settings</h3>
                <p class="text-body-2 text-medium-emphasis">Set the speed limit</p>
            </div>

            <v-text-field v-model="speedFormData['speed_threshold']" :rules="[vRules.required]"
                          ref="speedField"
                          :label="`Speed Threshold (${speedUnitLabel})`" type="number" variant="outlined"
                          prepend-inner-icon="mdi-speedometer" class="mb-2"
                          hint="Alert when the tracker goes over this speed" persistent-hint/>

            <v-text-field v-model="speedFormData['minimum_violations_in_seconds']" :rules="[vRules.required]"
                          label="Minimum Duration (seconds)" type="number" variant="outlined"
                          prepend-inner-icon="mdi-timer-outline" class="mb-2"
                          hint="How long the speed must be exceeded before triggering" persistent-hint/>

            <v-text-field v-model="speedFormData['minimum_alert_interval_seconds']" :rules="[vRules.required]"
                          label="Alert Cooldown (seconds)" type="number" variant="outlined"
                          prepend-inner-icon="mdi-clock-fast" class="mb-2"
                          hint="Minimum time between consecutive alerts" persistent-hint/>
        </template>

        <!-- Geofence Config -->
        <template v-if="type.toLowerCase() === 'geofence'">
            <div class="text-center mb-3">
                <v-icon size="36" color="blue" class="mb-2">mdi-map-marker-radius</v-icon>
                <h3 class="text-subtitle-1 font-weight-bold">Safe Zone</h3>
                <p class="text-body-2 text-medium-emphasis">Draw the zone on the map - a circle around a place, or a custom shape</p>
            </div>

            <div class="text-center mb-3">
                <p class="text-body-2 font-weight-medium mb-2">Notify me when they…</p>
                <v-btn-toggle v-model="enterOrExit" mandatory color="primary" variant="outlined"
                              divided rounded="lg" density="comfortable">
                    <v-btn :value="1">Arrive</v-btn>
                    <v-btn :value="2">Leave</v-btn>
                    <v-btn :value="3">Both</v-btn>
                    <v-btn :value="0">None</v-btn>
                </v-btn-toggle>
                <p v-if="enterOrExit === 0" class="text-caption text-medium-emphasis mt-2 mb-0">
                    The zone is saved without notifications - you'll still see when they're here.
                </p>
            </div>

            <v-card variant="outlined" rounded="lg" class="overflow-hidden">
                <div style="height: 350px">
                    <l-map ref="mapContainer" :center="mapCenter" :zoom="17" @ready="mapIsReady">
                        <l-tile-layer :url="mapConfig.streetUrl"/>
                    </l-map>
                </div>
            </v-card>

            <!-- No-Show: optionally alert if no tracker reaches this zone by a time -->
            <div class="mt-4">
                <v-switch v-model="noShowEnabled" color="primary" density="compact" hide-details
                          label="Alert me if they don't arrive by a time"/>
                <v-text-field v-if="noShowEnabled" v-model="geofenceFormData.arrive_by" type="time"
                              label="Arrive by" variant="outlined" density="compact" class="mt-2"
                              prepend-inner-icon="mdi-clock-alert-outline"
                              hint="On the days you set in the Schedule step. We'll alert you if no tracker reaches this zone by then."
                              persistent-hint/>
            </div>
        </template>
    </div>
</template>

<script setup>
import "leaflet-draw";
import {mapConfig} from "@/composables/mapConfig";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-geosearch/assets/css/leaflet.css";
import {LMap, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {GeoSearchControl, OpenStreetMapProvider} from "leaflet-geosearch";
import {ref, reactive, onMounted, watch, computed, nextTick} from "vue";
import {useAuthStore} from "@/stores/authStore";

// Build a geocodable string from the signed-in user's saved address so a new
// safe zone's map can open on their home. Returns null when nothing is on file.
function homeAddressQuery() {
    const u = useAuthStore().user;
    if (!u) return null;
    const parts = [u.addr_1, u.addr_city, u.addr_state, u.addr_postalcode, u.addr_country]
        .map((s) => (s ?? '').toString().trim())
        .filter((s) => s.length);
    return parts.length ? parts.join(', ') : null;
}

const userPrefersKm = computed(() => useAuthStore().configs?.distance_unit === 'km');
const speedUnitLabel = computed(() => userPrefersKm.value ? 'kph' : 'mph');
const mphToDisplay = (mph) => mph ? (userPrefersKm.value ? Math.round(mph * 1.609) : mph) : null;
const displayToMph = (val) => val ? (userPrefersKm.value ? Math.round(val / 1.609) : val) : null;
// Geofence trigger: 1=Arrive, 2=Leave, 3=Both, 0=None (zone saved, no alerts).
const enterOrExit = ref(3);
const speedField = ref(null);
// No-Show: when on, a deadline (arrive_by) is set on the zone and a scheduled
// server job alerts if no tracker reaches it by that time on the schedule's days.
const noShowEnabled = ref(false);
const props = defineProps({
    type: String,
    data: Object,
});
const mapContainer = ref();
const speedFormData = reactive({
    id: props.data?.id ?? null,
    // Pre-populate sensible defaults for a NEW speed alert (70 mph over 10s, with
    // a 5-min cooldown) so the user can save without filling every field; an
    // existing alert keeps its saved values.
    speed_threshold: mphToDisplay(props.data?.speed_threshold ?? 70),
    minimum_violations_in_seconds: props.data?.minimum_violations_in_seconds ?? 10,
    minimum_alert_interval_seconds: props.data?.minimum_alert_interval_seconds ?? 300,
});
const geofenceFormData = reactive({
    id: props.data?.id ?? null,
    type: props.data?.type ?? 0,
    enter_or_exit: 3,
    trigger_delay: 300,
    minimum_violation_time: 5,
    arrive_by: props.data?.arrive_by ?? null,
    radius: props.data?.radius ?? null,
    center_lat: props.data?.center_lat ?? null,
    center_lon: props.data?.center_lon ?? null,
    polygon_coordinates: props.data?.polygon_coordinates?.map((coordinate) => {
        return {...coordinate};
    }) ?? [],
});
const mapCenter = ref([(geofenceFormData.polygon_coordinates?.[0]?.lat ?? 47.41322), (geofenceFormData.polygon_coordinates?.[0]?.lng ?? -1.219482)]);
const emits = defineEmits(['setTemplate']);

function mapIsReady() {
    const map = mapContainer.value.leafletObject;
    map.attributionControl.remove();
    /*Add Draw Geo Search Control*/
    const provider = new OpenStreetMapProvider({searchUrl: mapConfig.searchUrl, reverseUrl: mapConfig.reverseUrl});
    const geoSearchOptions = new GeoSearchControl({
        style: "bar",
        autoClose: true,
        keepResult: true,
        showMarker: false,
        searchLabel: "Enter address",
        provider,
    });
    map.addControl(geoSearchOptions);

    // Put the cursor in the "Enter address" box so the user can type a place
    // straight away (after the control renders).
    nextTick(() => setTimeout(() => {
        map.getContainer().querySelector('.leaflet-control-geosearch input')?.focus();
    }, 200));

    // For a NEW safe zone (nothing drawn yet), start the map on the account's
    // home address instead of the world default. Editing keeps the saved centre.
    const isNewZone = geofenceFormData.center_lat == null
        && (geofenceFormData.polygon_coordinates?.length ?? 0) === 0;
    const homeQuery = homeAddressQuery();
    if (isNewZone && homeQuery) {
        provider.search({query: homeQuery}).then((results) => {
            if (results?.length) map.setView([results[0].y, results[0].x], 15);
        }).catch(() => { /* geocode failed; leave the default view */ });
    }


    /*Add Draw Tool Bar Control*/
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    const drawControl = new L.Control.Draw({
        draw: {
            circle: true,
            polygon: {
                showArea: true,
                allowIntersection: false, // Restricts shapes to simple polygons
                drawError: {
                    color: '#9d1212', // Color the shape will turn when intersects
                    message: '<strong>Oh snap!<strong> you can\'t draw that!' // Message that will show when intersect
                }
            },
            // disable toolbar item by setting it to false
            marker: false,
            polyline: false,
            rectangle: false,
            circlemarker: false,
        },
        edit: {
            featureGroup: drawnItems
        }
    });
    map.addControl(drawControl);

    if (geofenceFormData.type === 0 && geofenceFormData.center_lat != null) {
        drawnItems.addLayer(L.circle([geofenceFormData.center_lat, geofenceFormData.center_lon], {
            // editable: true,
            radius: geofenceFormData.radius,
        }));
        mapCenter.value = [geofenceFormData.center_lat, geofenceFormData.center_lon];
    } else if (geofenceFormData.type === 1 && geofenceFormData.polygon_coordinates) {
        const polygon = L.polygon(geofenceFormData.polygon_coordinates, {
            // editable: true,
        });
        drawnItems.addLayer(polygon);
        mapCenter.value = polygon.getBounds()['_northEast'];
    }

    map.on('draw:created', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });

        let type = e.layerType,
            layer = e.layer;

        if (type === 'circle') {
            geofenceFormData['type'] = 0;
            geofenceFormData['radius'] = layer.getRadius();
            geofenceFormData['center_lat'] = layer.getLatLng().lat;
            geofenceFormData['center_lon'] = layer.getLatLng().lng;
        } else if (type === 'polygon') {
            geofenceFormData['type'] = 1;
            geofenceFormData['polygon_coordinates'] = layer.getLatLngs()[0];
        }
        drawnItems.addLayer(layer);
    });

    map.on('draw:edited', function (e) {
        e.layers.eachLayer(function (layer) {
            if (layer instanceof L.Circle) {
                geofenceFormData['type'] = 0;
                geofenceFormData['radius'] = layer.getRadius();
                geofenceFormData['center_lat'] = layer.getLatLng().lat;
                geofenceFormData['center_lon'] = layer.getLatLng().lng;
            } else if (layer instanceof L.Polygon) {
                geofenceFormData['type'] = 1;
                geofenceFormData['polygon_coordinates'] = layer.getLatLngs()[0];
            }
        });
    });

    map.on('draw:deleted', function (e) {
        drawnItems.eachLayer(function (layer) {
            drawnItems.removeLayer(layer);
        });
        geofenceFormData['type'] = 0;
        geofenceFormData['radius'] = null;
        geofenceFormData['center_lat'] = null;
        geofenceFormData['center_lon'] = null;
        geofenceFormData['polygon_coordinates'] = null;
    });
}

onMounted(() => {
    if (props.type.toLowerCase() === 'speed') {
        // Focus the speed threshold box when this step is shown.
        nextTick(() => setTimeout(() => speedField.value?.focus?.(), 150));
        // immediate: emit the pre-filled defaults so the parent's alertInfo is
        // populated even if the user never edits the fields.
        watch(() => speedFormData, () => {
            // Convert display unit → MPH before emitting to parent
            emits('setTemplate', {
                ...speedFormData,
                speed_threshold: displayToMph(speedFormData.speed_threshold),
            });
        }, {deep: true, immediate: true})
    } else if (props.type.toLowerCase() === 'geofence') {
        if (mapContainer.value) {
            mapContainer.value.leafletObject?.setView(mapCenter.value, 17);
        }
        enterOrExit.value = props.data?.enter_or_exit ?? 3;
        geofenceFormData['enter_or_exit'] = enterOrExit.value;
        watch(enterOrExit, (val) => {
            geofenceFormData['enter_or_exit'] = val;
        });
        // No-Show: on for an existing rule with a deadline; clearing the toggle
        // drops the deadline so the zone goes back to a plain enter/exit zone.
        noShowEnabled.value = !!props.data?.arrive_by;
        watch(noShowEnabled, (on) => {
            if (!on) geofenceFormData['arrive_by'] = null;
        });
        watch(() => geofenceFormData, () => {
            emits('setTemplate', geofenceFormData);
        }, {deep: true})
    }
})
</script>